// GoldenFast API Service for VPS - Updated to use staging environment
// Based on reference PHP code pattern - STAGING VERSION
//
// Special Handling:
// - GID 37 (Dedicated Server USA): If API returns no data, use mock data
//   from MOCK_DATA['37'] to ensure products are always available

// Constants from reference PHP (exact copy)
const TERMIN = [
  "monthly",
  "quarterly",
  "semiannually",
  "annually",
  "biennially",
  "triennially",
];

const TERMIN_SETUP = [
  "msetupfee",
  "qsetupfee",
  "ssetupfee",
  "asetupfee",
  "bsetupfee",
  "tsetupfee",
];

const HIDDEN_PID = [
  147, 139, 142, 176, 131, 133, 246,
  // ...[118, 121,219],
  // ...[142, 170, 187, 151, 144, 165, 145, 166, 149, 148,141, 150, 152, 175,171,172,173,169,174],
  // ...[71,14,15,16,17],
  // ...[21,22,23,24,25],
  // ...[126,127,128,129,130]
];

const SLIDER_PID = [25, 26];
const HIDDEN_ADDON_ID = [118, 121, 219];

const RELATION_ADDONS_OS = {
  cloudlinux: ["cpanel", "solusvm", "softaculous"],
  windows: ["plesk", "sharepoint", "sqlserver"],
  centos: [
    "plesk",
    "cpanel",
    "solusvm",
    "directadmin",
    "webuzo",
    "softaculous",
  ],
  debian: ["webuzo", "plesk", "directadmin"],
  ubuntu: ["plesk", "directadmin"],
  fedora: ["plesk", "directadmin", "webuzo"],
  freebsd: ["directadmin"],
  almalinux: ["cpanel"],
};

const ASUSTOR_PRODUCTS = [383, 384, 385, 386, 387, 388];

// API Configuration - Dynamic based on environment
const getApiConfig = () => {
  // Check current environment and use appropriate settings
  const environment = process.env.REACT_APP_ENVIRONMENT || "staging-gfn";

  // Get base URL from environment variable first, fallback to environment-based logic
  const baseApiUrl = process.env.REACT_APP_BASE_API || process.env.REACT_APP_GFN_BASE_URL;

  if (baseApiUrl) {
    // Extract base URL from REACT_APP_BASE_API or REACT_APP_GFN_BASE_URL
    // Format examples:
    // - https://client.goldenfast.net/order/apis/ -> https://client.goldenfast.net
    // - https://client.goldenfast.net/order/ -> https://client.goldenfast.net
    // - https://staging.client.goldenfast.net/order/apis/ -> https://staging.client.goldenfast.net

    let baseUrl = baseApiUrl.trim();

    // Remove trailing slashes
    baseUrl = baseUrl.replace(/\/+$/, '');

    // Remove /order/apis or /order/apis/ or /order/ from the end
    baseUrl = baseUrl.replace(/\/order\/apis\/?$/, '');
    baseUrl = baseUrl.replace(/\/order\/?$/, '');

    // Ensure it starts with http:// or https://
    if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
      baseUrl = 'https://' + baseUrl;
    }

    return {
      baseUrl: baseUrl,
      apiPath: "/order/apis/",
      endpoints: {
        products: "cekHiddenProduct.php",
        addons: "getAddons.php",
      },
    };
  }

  // Fallback to environment-based logic if no environment variable is set
  switch (environment) {
    case "production-gfn":
      return {
        baseUrl: "https://client.goldenfast.net",
        apiPath: "/order/apis/",
        endpoints: {
          products: "cekHiddenProduct.php",
          addons: "getAddons.php",
        },
      };

    case "staging-gfn":
    default:
      return {
        baseUrl: "https://staging.client.goldenfast.net",
        apiPath: "/order/apis/",
        endpoints: {
          products: "cekHiddenProduct.php",
          addons: "getAddons.php",
        },
      };
  }
};

// Helper function to build API URLs
const buildApiUrl = (endpoint, params = {}) => {
  // Get current API config based on environment
  const config = getApiConfig();

  // Use the configured base URL and API path
  const baseUrl = config.baseUrl;
  const apiPath = config.apiPath;

  let url = `${baseUrl}${apiPath}${endpoint}`;

  // Add query parameters with safety checks
  if (params && typeof params === "object") {
    const queryParams = new URLSearchParams(params);
    if (queryParams.toString()) {
      url += `?${queryParams.toString()}`;
    }
  }

  return url;
};

// API Service functions (following reference PHP pattern)
export const goldenFastAPI = {
  // Load products by group ID - API only
  loadProductsByGroup: async (gid) => {
    const config = getApiConfig();
    const apiUrl = buildApiUrl(config.endpoints.products, { gid });

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: { Accept: "application/json" },
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const data = await response.json();

    if (data.status === false) {
      return { status: false, data: [] };
    }

    return data;
  },

  // Load product addons by PID - API only
  loadProductAddons: async (pid) => {
    const config = getApiConfig();
    const apiUrl = buildApiUrl(config.endpoints.addons, { pid });

    const response = await fetch(apiUrl, {
      method: "GET",
      headers: { Accept: "application/json" },
      mode: "cors",
    });

    if (!response.ok) {
      throw new Error(
        `Addons API request failed with status ${response.status}`
      );
    }

    const data = await response.json();
    return data;
  },

  // Convert termin to setup fee field name (from reference PHP)
  convertTerminToFee: (termin) => {
    switch (termin) {
      case TERMIN[0]:
        return "msetupfee";
      case TERMIN[1]:
        return "qsetupfee";
      case TERMIN[2]:
        return "ssetupfee";
      case TERMIN[3]:
        return "asetupfee";
      case TERMIN[4]:
        return "bsetupfee";
      case TERMIN[5]:
        return "tsetupfee";
      default:
        return "msetupfee";
    }
  },

  // Convert termin to readable format (exact copy from reference PHP)
  convertTermin: (termin) => {
    switch (termin) {
      case TERMIN[0]:
        return "1 Bulan";
      case TERMIN[1]:
        return "3 Bulan";
      case TERMIN[2]:
        return "6 Bulan";
      case TERMIN[3]:
        return "1 Tahun";
      case TERMIN[4]:
        return "2 Tahun";
      case TERMIN[5]:
        return "3 Tahun";
      default:
        return termin;
    }
  },

  // Format currency (exact copy from reference PHP)
  formatRupiah: (number, isRp = true) => {
    number = parseInt(number);
    if (number % 1 !== 0) {
      number = number.toFixed(0);
    }
    const result = new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
    })
      .format(number)
      .replace(/\D00$/, "");

    if (isRp) {
      return result;
    } else {
      return result.replace("Rp", "").replace(/\s/g, "");
    }
  },

  // Parse product description
  parseProductDescription: (description) => {
    if (!description) return [];

    let arr_data = description.split("</br>");
    arr_data = arr_data.filter((v) => v !== "");

    return arr_data.map((item) => {
      let cleanItem = item.trim();
      // Handle Easy Managed link
      if (cleanItem === "Easy Managed") {
        return {
          text: cleanItem,
          isEasyManaged: true,
        };
      }
      return {
        text: cleanItem,
        isEasyManaged: false,
      };
    });
  },

  // Get OS-related addons
  getOSRelatedAddons: (osName) => {
    const keys = Object.keys(RELATION_ADDONS_OS);
    const matchedKey = keys.find((key) => osName.toLowerCase().includes(key));

    return matchedKey ? RELATION_ADDONS_OS[matchedKey] : [];
  },

  // Get group info by ID
  getGroupInfo: (gid) => {
    const groups = {
      15: { name: "Dedicated Server Indonesia" },
      37: { name: "Dedicated Server USA" },
      4: { name: "Colocation Server" },
      26: { name: "Cloud VPS Corporate" },
      27: { name: "Leased To Owned Dedicated Bare Metal Server" },
      29: { name: "Cloud VPS corporate - JKT2" },
      31: { name: "Cloud VPS corporate - WestJavaBDG-1" },
    };

    return groups[gid] || { name: `Group ${gid}` };
  },

  // Filter products based on HIDDEN_PID rules
  filterProducts: (products) => {
    return products.filter((product) => {
      const pid = parseInt(product.pid);
      return !HIDDEN_PID.includes(pid);
    });
  },

  // Filter addons based on HIDDEN_ADDON_ID rules
  filterAddons: (addons) => {
    return addons.filter((addon) => {
      return !HIDDEN_ADDON_ID.includes(parseInt(addon.id));
    });
  },
};

// Helper functions for URL and other utilities (from reference PHP)
export const getURLParams = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const params = {};
  for (const [key, value] of urlParams) {
    params[key] = value;
  }
  return params;
};

export const getBaseFileName = (path = window.location.pathname) => {
  const url = path;
  let arr_url = url.split("/");
  arr_url = arr_url.filter((v) => v !== "");
  const base = arr_url[arr_url.length - 1];
  return base;
};

export const getBaseDirectory = (path = window.location.pathname) => {
  const url = path;
  let arr_url = url.split("/");
  arr_url = arr_url.filter((v) => v !== "");
  const base = arr_url[0];
  return base;
};

// Export all constants and main API
export {
  TERMIN,
  TERMIN_SETUP,
  HIDDEN_PID,
  HIDDEN_ADDON_ID,
  RELATION_ADDONS_OS,
  SLIDER_PID,
  ASUSTOR_PRODUCTS,
  getApiConfig,
  buildApiUrl,
};
